import { LightBulbIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import {
  groupsMissingReverse,
  joinPreviewText,
  openHalvesHint,
  pairedFormatFor,
  planReverseJoins,
  R1_HALVES_ACK,
  reverseJoinPreview
} from '../../../Services/OrderWizard/reverseFiles.ts'
import { countItems, sampleDisplayName } from '../../../Services/OrderWizard/sampleLabels.ts'
import { useAddFiles } from './AddFilesContext.ts'
import { RETRY_BUTTON } from './retryStyle.ts'

const LINK = `${RETRY_BUTTON} px-0`

/** Opens "Add files", where the R2 files are dropped; they find their samples by name. */
export const AddReverseFilesButton = ({ testId = 'wizard-add-r2' }: { testId?: string }) => {
  const { open } = useAddFiles()
  return (
    <button type="button" className={LINK} data-testid={testId} onClick={open}>
      Add R2 files
    </button>
  )
}

/** On the Files blocker for samples missing their R2: add the files, or pair with those the order already holds. */
export const ReverseMissingActions = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const joins = planReverseJoins(draft).length
  return (
    <span className="ml-5 flex flex-wrap items-center gap-x-3">
      <AddReverseFilesButton />
      {joins > 0 && (
        <span className="text-xs text-slate-600">
          <button
            type="button"
            className={LINK}
            data-testid="wizard-pair-r2-by-name"
            onClick={() => dispatch(wizard.reversePairingRequested({ draftId: draft.id }))}
          >
            Pair by file name
          </button>{' '}
          ({joinPreviewText(reverseJoinPreview(draft))})
        </span>
      )}
    </span>
  )
}

/**
 * Merged reads that look like R1 halves: a quiet recommendation, never a switch made for the lab. The one
 * click switches the order to paired-end and opens "Add files" for the R2 files.
 */
export const R1HalvesNotice = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const { open } = useAddFiles()
  const hint = openHalvesHint(draft)
  const paired = pairedFormatFor(draft)
  if (!hint || !paired) {
    return null
  }
  const preview = joinPreviewText(hint.preview)
  return (
    <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-sm text-slate-700" data-testid="wizard-r1-halves">
      <LightBulbIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
      <div className="space-y-1">
        <p className="font-medium text-slate-800">These look like R1 halves — add the R2 files</p>
        <p>{hint.reason} If the R2 files exist, switch the order to paired-end and drop them; they find their samples by name.</p>
        {preview && (
          <p className="text-xs text-slate-600" data-testid="wizard-r1-halves-preview">
            Switching: {preview}.
          </p>
        )}
        <p className="flex flex-wrap gap-x-3">
          <button
            type="button"
            className={LINK}
            data-testid="wizard-r1-halves-switch"
            onClick={() => {
              dispatch(wizard.settingChanged({ draftId: draft.id, change: { setting: 'sampleConfiguration', value: paired } }))
              open()
            }}
          >
            Switch to paired-end and add the R2 files
          </button>
          <button
            type="button"
            className={LINK}
            data-testid="wizard-r1-halves-dismiss"
            onClick={() => dispatch(wizard.warningsAcknowledged({ draftId: draft.id, keys: [R1_HALVES_ACK] }))}
          >
            They are merged reads
          </button>
        </p>
      </div>
    </div>
  )
}

/** On Review: the samples that still miss their R2, by name, with the way to add them. */
export const MissingReverseList = ({ draft }: { draft: DraftState }) => {
  const missing = groupsMissingReverse(draft)
  if (missing.length === 0) {
    return null
  }
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-slate-800" data-testid="wizard-review-missing-r2">
      <p>
        {countItems(missing)} {missing.length === 1 ? 'is' : 'are'} missing {missing.length === 1 ? 'its' : 'their'} R2 file:{' '}
        <span data-testid="wizard-review-missing-r2-names">{missing.map((group) => sampleDisplayName(draft, group.key)).join(', ')}</span>
      </p>
      <AddReverseFilesButton testId="wizard-review-add-r2" />
    </div>
  )
}
